import React from 'react';
import {useDispatch} from 'react-redux';
import {Typography} from '@mui/material';
import {messageAdd, MessageTypes} from '../../../store/message';
import {requestSecurityAPI} from '../../../store/auth/api';
import {UserType, ExternalUserType} from 'hemwb-api';
import {hardCodedOptions} from '../../../components/pages/model/common/ModelEdit/ModelEditMetadata/constants';
import {UserSelectHandler, AnyUserDTO} from './utils';

type AutocompleteItemProps = {
  user?: AnyUserDTO;
  primary: string;
  secondary: string;
  ownershipType?: string;
  isid?: string;
  modelUuid?: string;
  theoremModel?: boolean;
  subModelAccess?: boolean;
  subModelEnable?: boolean;
  fileUuid?: string;
  name?: string;
  userType?: string;
  externalUserType?: string;
  handleSecurityAPI?: () => void;
  restrictAccessOfExternalUser?: (name: string) => void;
  informationClassification?: string;
  onSelectGeneric?: UserSelectHandler; // Generic user selection handler
};

export const AutocompleteItem: React.FC<AutocompleteItemProps> = ({
  user,
  primary,
  secondary,
  ownershipType,
  isid,
  modelUuid,
  theoremModel,
  subModelAccess,
  subModelEnable,
  fileUuid,
  userType,
  externalUserType,
  handleSecurityAPI,
  restrictAccessOfExternalUser,
  informationClassification,
  name,
  onSelectGeneric,
  ...rest
}) => {
  const dispatch = useDispatch();
  const classificationOptions = hardCodedOptions['information_classification'];
  const errorText = theoremModel ? 'Theorem model' : 'Sensitive model';

  const handleOnClick = () => {
    if (subModelEnable && !subModelAccess) {
      dispatch(
        messageAdd(
          `Selected User doesn't have the access to this Sub Model. Please contact Editor to grant access
      to the Sub Model.`,
          MessageTypes.ERROR,
        ),
      );
    }
    if (
      restrictAccessOfExternalUser &&
      name &&
      userType === UserType.EXTERNALUSER &&
      !['modelSteward', 'vestLead'].includes(name)
    ) {
      if (['viewers', 'owners'].includes(name) && externalUserType === ExternalUserType.HTA) {
        setTimeout(() => restrictAccessOfExternalUser(name), 100);
        dispatch(messageAdd(`HTA User cannot be assigned as a Model ${name}.`, MessageTypes.ERROR));
      }

      if (theoremModel || (classificationOptions && informationClassification === classificationOptions[3])) {
        setTimeout(() => restrictAccessOfExternalUser(name), 100);
        dispatch(messageAdd(`External user access cannot be added as this is a ${errorText}.`, MessageTypes.ERROR));
      }
    } else if (isid && modelUuid && theoremModel && handleSecurityAPI) {
      requestSecurityAPI({securityAPIDTO: {isid: isid, modelUUID: modelUuid, fileUuid: fileUuid}})
        .then(() => {
          if (user) {
            onSelectGeneric?.(user);
          }
        })
        .catch((error) => {
          if (error.status === 500) {
            handleSecurityAPI();
            dispatch(
              messageAdd(
                `This User must request access in THEOREM. Please have the User <a href="https://collaboration.merck.com/sites/THEOREM/Lists/FAQ%20%20THEOREM/DispForm.aspx?ID=53&e=mvH1Me">follow these steps</a> to request access in THEOREM.`,
                MessageTypes.ERROR,
              ),
            );
          }
        });

      return;
    }

    // If all conditions pass, allow selection
    if (user) {
      onSelectGeneric?.(user);
    }
  };

  return (
    <div {...rest} style={{width: '100%', cursor: 'pointer'}} onClick={handleOnClick}>
      {ownershipType && <Typography variant="body1">{ownershipType}</Typography>}
      <Typography variant="body1">{primary}</Typography>
      <Typography variant="body2" color="textSecondary">
        {secondary}
      </Typography>
    </div>
  );
};
